import type { BackendClient } from '../main/bridge/backend-client';
import type { StreamFrame } from '../core/bridge/types';
import type { TypedValue } from '../core/bridge/connect-client';

/*
 * ONE value subscription per controller, fanned out to every tile that wants it.
 *
 * A widget host used to open its own `Values.Subscribe` stream. That reads
 * naturally - each tile asks for what it needs - and it does not survive
 * contact with a real dashboard: sixty tiles opened sixty streams, and because
 * each one is an HTTP request the dev proxy answers by dialling the gateway
 * afresh, most of them died in the TLS handshake. The board came up empty and
 * slow, and the cause was not the gateway being slow, it was being asked sixty
 * times for the same thing.
 *
 * Tiles on a dashboard overwhelmingly read the SAME controller, and often the
 * very same symbol. So the subscription belongs to the page, not the tile: one
 * stream per (gateway, controller), carrying the union of what the tiles want,
 * with each tile handed the frames it asked for.
 *
 * This is the fanout the rest of the app already does one layer down - the
 * backend worker shares a changes consumer per controller between views. This
 * is the same idea applied between tiles of one view, and it is why a
 * hundred-tile wall display costs one stream.
 *
 * WHAT THIS IS NOT: it is not a widening of anyone's access. The union is only
 * ever what tiles asked for, each subscriber is handed only the symbols IT
 * named, and the capability grant is still checked by the host afterwards. A
 * tile cannot see another tile's symbols by sharing a pipe with it.
 */

/** A subscriber's view of the stream: the frames it asked for, and nothing else. */
export type ValueSink = (frame: StreamFrame<TypedValue>) => void;

interface Member {
  symbols: Set<string>;
  sink: ValueSink;
}

interface Group {
  members: Set<Member>;
  /** The union currently subscribed. Reopened when a member wants more. */
  open: Set<string>;
  port?: MessagePort;
  /** Last value per symbol, so a tile mounting late paints immediately. */
  last: Map<string, TypedValue>;
  /** Highest revision seen, so a replayed snapshot keeps the stream's ordering. */
  revision: number;
  /** Pending open, so a burst of mounting tiles becomes one request. */
  timer: number;
  generation: number;
}

const groups = new Map<string, Group>();

const keyOf = (gatewayId: string, controllerId: string): string => `${gatewayId}\0${controllerId}`;

/**
 * How long to wait before opening the stream.
 *
 * Tiles mount in a burst - a sixty-tile board declares its symbols over a few
 * milliseconds - so opening on the first request would mean sixty reopenings as
 * each subsequent tile widens the set. Collecting first turns that into one.
 * Short enough that a single-tile page is not perceptibly delayed.
 */
const COALESCE_MS = 60;

/**
 * Subscribe to values for `symbols` on one controller. Returns a function that
 * releases this subscriber's interest; the underlying stream closes once the
 * last one lets go.
 */
export function subscribeValues(
  backend: BackendClient,
  gatewayId: string,
  controllerId: string,
  symbols: string[],
  sink: ValueSink,
): () => void {
  const key = keyOf(gatewayId, controllerId);
  let group = groups.get(key);
  if (!group) {
    group = { members: new Set(), open: new Set(), last: new Map(), revision: 0, timer: 0, generation: 0 };
    groups.set(key, group);
  }

  const member: Member = { symbols: new Set(symbols), sink };
  group.members.add(member);

  // Anything already known is delivered at once, as a snapshot. A tile that
  // mounts after the stream is running would otherwise sit on its placeholder
  // until the next change, which for a symbol that moves twice an hour reads as
  // a broken tile rather than a quiet one.
  const known = symbols.map((s) => group.last.get(s)).filter((v): v is TypedValue => !!v);
  if (known.length) queueMicrotask(() => { if (group.members.has(member)) sink({ kind: 'snapshot', items: known, revision: group.revision }); });

  const missing = symbols.some((s) => !group.open.has(s));
  if (missing) schedule(backend, gatewayId, controllerId, group);

  return () => {
    group.members.delete(member);
    if (group.members.size === 0) {
      group.port?.postMessage({ kind: 'close' });
      group.port?.close();
      clearTimeout(group.timer);
      groups.delete(key);
    }
    // A member leaving does NOT narrow the stream. Reopening to drop a few
    // symbols costs a round trip and a gap in delivery for every remaining
    // tile, to save the gateway sending values it is already producing.
  };
}

function schedule(backend: BackendClient, gatewayId: string, controllerId: string, group: Group): void {
  clearTimeout(group.timer);
  // setTimeout, not window.setTimeout: this module holds no other DOM
  // dependency, and keeping it that way is what lets the cost and isolation
  // properties be tested without a browser.
  group.timer = setTimeout(() => void reopen(backend, gatewayId, controllerId, group), COALESCE_MS) as unknown as number;
}

async function reopen(backend: BackendClient, gatewayId: string, controllerId: string, group: Group): Promise<void> {
  const wanted = new Set<string>();
  for (const m of group.members) for (const s of m.symbols) wanted.add(s);
  if (wanted.size === 0) return;

  const generation = ++group.generation;
  const previous = group.port;
  const port = await backend.api.subscribeGridValues(gatewayId, controllerId, [...wanted]);

  // A newer reopen started while this one was in flight, or every member left.
  if (generation !== group.generation || group.members.size === 0) {
    port.postMessage({ kind: 'close' });
    port.close();
    return;
  }

  // The old stream is closed only once its replacement exists, so a widening
  // reopen does not blind the tiles that were already being served.
  previous?.postMessage({ kind: 'close' });
  previous?.close();

  group.port = port;
  group.open = wanted;
  port.onmessage = (e: MessageEvent<StreamFrame<TypedValue>>) => deliver(group, e.data);
  port.start();
}

function deliver(group: Group, frame: StreamFrame<TypedValue>): void {
  const items: TypedValue[] =
    frame.kind === 'snapshot' ? frame.items
    : frame.kind === 'delta' && frame.op === 'upsert' ? [frame.item]
    : [];
  if (!items.length) {
    // Errors and closes concern every member equally.
    if (frame.kind === 'error') for (const m of group.members) m.sink(frame);
    return;
  }
  for (const v of items) group.last.set(v.symbolId, v);
  if (frame.kind === 'snapshot') group.revision = frame.revision;

  for (const m of group.members) {
    // Each member sees only what it named. The union exists so the gateway is
    // asked once; it is not a wider view for anybody.
    const mine = items.filter((v) => m.symbols.has(v.symbolId));
    if (mine.length) m.sink({ kind: 'snapshot', items: mine, revision: group.revision });
  }
}

/** Test seam: forget every group, as a fresh page would. */
export function resetValueFanout(): void {
  for (const g of groups.values()) { clearTimeout(g.timer); g.port?.close(); }
  groups.clear();
}
